import './SecurityBadges.css';

/**
 * TRUST SEALS.
 *
 * Deliberately not links. Nobody clicks a trust seal, and a link that opens a
 * third-party scan page is a worse experience than a seal that simply reads as
 * part of the site.
 *
 * That puts all the weight on the wording, so every line here is a statement
 * about this site that is true right now and that someone could check for
 * themselves if they cared to. What is NOT here is any third-party grade —
 * no "SSL Labs A", no "securityheaders A". Printing a grade the site has not
 * earned is inventing a credential, and it is the one thing that turns a seal
 * from reassurance into a lie. When the real grades reach A they can be quoted
 * here; until then these stand on their own.
 */

const Shield = ({ size = 22 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true" focusable="false">
    <path
      fill="currentColor"
      d="M12 1.6 4 4.9v6.2c0 4.9 3.4 9.5 8 10.6 4.6-1.1 8-5.7 8-10.6V4.9l-8-3.3Zm-1.2 14.2-3.3-3.3 1.4-1.4 1.9 1.9 4.7-4.7 1.4 1.4-6.1 6.1Z"
    />
  </svg>
);

const Lock = ({ size = 22 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true" focusable="false">
    <path
      fill="currentColor"
      d="M17 9V7a5 5 0 0 0-10 0v2H5.6A1.6 1.6 0 0 0 4 10.6v9.8A1.6 1.6 0 0 0 5.6 22h12.8a1.6 1.6 0 0 0 1.6-1.6v-9.8A1.6 1.6 0 0 0 18.4 9H17ZM9 7a3 3 0 0 1 6 0v2H9V7Zm4 9.7V19h-2v-2.3a2 2 0 1 1 2 0Z"
    />
  </svg>
);

const Key = ({ size = 22 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true" focusable="false">
    <path
      fill="currentColor"
      d="M12 1a5 5 0 0 0-5 5c0 .6.1 1.2.3 1.7L2 13v4h4v-2h2v-2h2l1.3-1.3c.5.2 1.1.3 1.7.3a5 5 0 0 0 0-10Zm2 4.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3Z"
    />
  </svg>
);

const Card = ({ size = 22 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true" focusable="false">
    <path
      fill="currentColor"
      d="M3 5h18a1 1 0 0 1 1 1v3H2V6a1 1 0 0 1 1-1Zm-1 6h20v7a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-7Zm3 4v2h5v-2H5Z"
    />
  </svg>
);

/**
 * Each seal: an icon block, a headline, and a smaller line of detail. The two
 * lines are what make it read as a seal rather than a chip.
 */
const SEALS = {
  ssl: { Icon: Shield, title: 'SSL SECURED', detail: 'TLS 1.3 encrypted' },
  otp: { Icon: Key, title: 'VERIFIED LOGIN', detail: 'One-time code required' },
  session: { Icon: Lock, title: 'PROTECTED SESSION', detail: 'Signs out when idle' },
  payment: { Icon: Card, title: 'SAFE CHECKOUT', detail: 'We never store card details' },
};

function Seal({ which }) {
  const seal = SEALS[which];
  if (!seal) return null;
  const { Icon, title, detail } = seal;

  return (
    <div className="sec-seal">
      <div className="sec-seal__badge" aria-hidden="true">
        <Icon />
      </div>
      <div className="sec-seal__text">
        <span className="sec-seal__title">{title}</span>
        <span className="sec-seal__detail">{detail}</span>
      </div>
    </div>
  );
}

const LAYOUTS = {
  login: ['ssl', 'otp'],
  footer: ['ssl', 'otp', 'session', 'payment'],
  payment: ['ssl', 'payment'],
  inline: ['ssl', 'otp'],
};

/**
 * @param {'login'|'footer'|'inline'|'payment'} variant where it is being shown.
 */
export default function SecurityBadges({ variant = 'inline' }) {
  const seals = LAYOUTS[variant] || LAYOUTS.inline;

  return (
    <div className={`sec-seals sec-seals--${variant}`}>
      {seals.map((key) => (
        <Seal key={key} which={key} />
      ))}
    </div>
  );
}
